import { defineStore } from 'pinia';
import { computed, reactive } from 'vue';
import { GroupRepository } from '@/coreInfrastructureModule/Repositories/GroupRepository';
import { GetGroupUseCase } from '../useCases/getGroupUseCase';
import { IGroup } from '@/coreDomainsModule/entities/group';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { FilterInfoState } from '@/corePagesModule/SelectInput/interface/uiState';

/**
 * 集計グループのリストのStoreを返す
 * @param groupTag - グループのタグ
 */
export const useGroupListMultipleSelectInputStore = (groupTag: string) =>
  defineStore(`groupListMultipleSelectInput-${groupTag}`, () => {
    const state = reactive<IGroupList>({
      groupList: [],
      selectedGroupIds: [],
      selectInputState: false,
      pagingState: { type: 'none' },
      pagingOptions: initPagingOptions(15),
    });

    /**
     * リセット
     */
    const $reset = () => {
      state.groupList = [];
      state.selectedGroupIds = [];
      state.selectInputState = false;
      state.pagingState = { type: 'none' };
      state.pagingOptions = initPagingOptions(15);
    };

    /**
     * 初期化
     */
    const init = async () => {
      $reset();
      await fetchGroupList();
    };

    /**
     * グループデータを再取得する。選択中のグループがある場合はそのグループを選択した状態にして表字名を更新する
     */
    const reloadGroupData = async () => {
      await fetchGroupList();
      // 選択中のグループがある場合はそのグループの選択状態は維持する
      _setSelectedGroupId(state.selectedGroupIds);
    };

    /**
     * ページング情報をリセットする
     */
    const resetSelectInputState = () => {
      state.pagingState = { type: 'none' };
      state.pagingOptions = initPagingOptions(15);
    };

    /**
     * グループリストを取得する
     */
    const fetchGroupList = async () => {
      const offset = state.pagingOptions.nextOffset ?? state.pagingOptions.offset;

      if (offset === 0) {
        // ファーストローディング
        state.pagingState = {
          type: 'loading',
        };
      } else if (state.pagingState.type === 'success') {
        state.pagingState = {
          type: 'success',
          footerLoadingState: 'loading',
          data: state.pagingState.data,
        };
      }

      // オフセットを更新
      state.pagingOptions = {
        ...state.pagingOptions,
        offset: offset,
      };

      // リポジトリを取得
      const repository = new GroupRepository();
      const useCase = new GetGroupUseCase();
      // APIを叩く
      const result = await useCase.ExecuteMock(repository); // TODO: モックなので後で置き換える

      switch (result.status.type) {
        case 'success':
          // データをセット
          state.groupList = result.status.data.groups;
          state.pagingOptions = result.status.data.pagingOptions;
          if (state.groupList.length === 0) {
            state.pagingState = {
              type: 'zeroMatch',
            };
          } else {
            if (offset === 0) {
              // ファーストローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: state.groupList.map((group) => group.groupName),
              };
            } else if (state.pagingState.type === 'success') {
              // 追加ローディング
              state.pagingState = {
                type: 'success',
                footerLoadingState: 'append',
                data: state.pagingState.data.concat(state.groupList.map((group) => group.groupName)),
              };
            }
          }
          break;
        case 'error':
          if (offset === 0) {
            state.pagingState = {
              type: 'error',
              errorMessage: 'フィルター情報の取得に失敗しました', // TODO: APIからのエラーメッセージをハンドリング
            };
          } else if (state.pagingState.type === 'success') {
            state.pagingState = {
              type: 'success',
              footerLoadingState: 'error',
              data: state.pagingState.data,
            };
          }
          break;
      }
    };

    /**
     * グループが選択された際の処理
     * @param selectedIndexes - 選択されたグループのインデックス
     */
    const onSelectedGroup = (selectedIndexes: number[]) => {
      // 選択されたグループを取得
      _setSelectedGroupId(selectedIndexes.map((index) => state.groupList[index].groupId));
    };

    /**
     * グループの値が更新された際の処理
     */
    const onSelectedDefaultValue = () => {
      _setSelectedGroupId([]);
    };

    // computed
    const groupListNames = computed((): string[] => state.groupList.map((group) => group.groupName));

    const selectedGroupNames = computed((): string[] => {
      return state.groupList.filter((group) => state.selectedGroupIds.includes(group.groupId)).map((group) => group.groupName);
    });

    const selectedIndexes = computed((): number[] => {
      return state.groupList.reduce((acc, group, index) => {
        if (state.selectedGroupIds.includes(group.groupId)) {
          acc.push(index);
        }
        return acc;
      }, [] as number[]);
    });

    // private
    /**
     * groupIdのsetter
     * @param groupIds - グループID
     */
    const _setSelectedGroupId = (groupIds: string[]) => {
      state.selectedGroupIds = groupIds;
    };

    /**
     * groupIdのsetter
     * @param groupId - グループID
     */
    const _addSelectedGroupId = (groupId: string) => {
      state.selectedGroupIds.push(groupId);
    };

    return {
      state,
      $reset,
      init,
      reloadGroupData,
      resetSelectInputState,
      fetchGroupList,
      onSelectedGroup,
      onSelectedDefaultValue,
      groupListNames,
      selectedGroupNames,
      selectedIndexes,
    };
  });

/**
 * 集計グループのリスト
 */
interface IGroupList {
  /**
   * 集計グループのリスト
   */
  groupList: IGroup[];
  /**
   * 選択されたグループのID
   */
  selectedGroupIds: string[];
  /**
   * SelectInputの表示状態
   */
  selectInputState: boolean;
  /**
   * ページング情報
   */
  pagingOptions: IPagingOptions;
  /**
   * ページングの状態
   */
  pagingState: FilterInfoState;
}
